import { ChangeEvent, Fragment, TextareaHTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DailyTotals } from '@/types'
import { PlanData } from '@/services/analyticsService'
import { Card } from '@/components/ui'
import { formatNumber, getCurrencySymbol } from '@/lib/utils'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { supabase } from '@/lib/supabase'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'
import { AlertCircle, AlertTriangle, Check, CheckCircle2, Info, Loader2, Pencil } from 'lucide-react'

/**
 * Переделанная таблица метрик: строки — метрики по отделам, колонки — дни выбранной
 * недели + итог недели план/факт, плюс ограничитель и решение по каждой метрике.
 * Пилот: включается только для студий пилотного города (см. DailyPeriodMetricsTable.tsx).
 */
export interface WeeklyDepartmentMetricsTableProps {
  dailyData: DailyTotals[]
  planData: PlanData | null
  weeklyPlans: WeeklyPlanVsFactRow[]
  studioId?: number | null
  studioIds?: number[]
  canEditDecisions?: boolean
  canViewDecisions?: boolean
  missingPlanStudios?: string[]
  zeroRevenueStudios?: string[]
  zeroPlanRevenueStudios?: string[]
  hideDelta?: boolean
  hideDeltaStudioCount?: number
  currency?: string
  selectedRangeMode?: 'period' | 'week' | 'last_3_days'
  selectedRangeWeekNumber?: number | null
  onSelectedWeekNumberChange?: (weekNumber: number) => void
}

type MetricKey =
  | 'marketing'
  | 'leads'
  | 'answerRate'
  | 'appointments'
  | 'newVisitors'
  | 'newSales'
  | 'renewals'
  | 'upgrades'
  | 'returning'
  | 'newSubscriptions'
  | 'subscriptionRenewals'
  | 'subscriptionUpgrades'
  | 'subscriptionReturning'
  | 'productVisits'

type MetricKind = 'count' | 'currency' | 'countCurrency' | 'percent'
type KpiKind = 'count' | 'currency' | 'percent'
type DecisionTone = 'limiter' | 'decision'

interface MetricConfig {
  key: MetricKey
  group: string
  label: string
  kind: MetricKind
}

interface MetricValue {
  count?: number
  amount?: number
  value?: number | null
}

interface WeekOption {
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
  source?: WeeklyPlanVsFactRow
}

interface DecisionRow {
  metric_key: string
  limiter: string | null
  decision: string | null
}

type DecisionDraft = { limiter: string; decision: string }
type DecisionSaveState = 'saving' | 'saved' | 'error' | undefined
type DecisionTextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> & {
  value: string
  onChange: (value: string) => void
}

interface RatingDailyRow {
  studio_internal_id: number
  metric_date: string
  admissions_count: number | null
}

interface RatingWeeklyRow {
  studio_internal_id: number
  admissions_count: number | null
}

interface KpiCard {
  key: string
  label: string
  kind: KpiKind
  plan: number | null
  fact: number | null
}

const METRICS: MetricConfig[] = [
  { key: 'marketing', group: 'Маркетинг', label: 'Бюджет на маркетинг', kind: 'currency' },
  { key: 'leads', group: 'Маркетинг', label: 'Заявки', kind: 'count' },
  { key: 'answerRate', group: 'Маркетинг', label: '% дозвона', kind: 'percent' },
  { key: 'appointments', group: 'Администраторы', label: 'Записи', kind: 'count' },
  { key: 'newVisitors', group: 'Администраторы', label: 'Приходы', kind: 'count' },
  { key: 'newSales', group: 'Администраторы', label: 'Продажа абонементов', kind: 'countCurrency' },
  { key: 'renewals', group: 'Администраторы', label: 'Продления', kind: 'countCurrency' },
  { key: 'upgrades', group: 'Администраторы', label: 'Апгрейды', kind: 'countCurrency' },
  { key: 'returning', group: 'Администраторы', label: 'БЧС', kind: 'countCurrency' },
  { key: 'newSubscriptions', group: 'Администраторы', label: 'Новые подписки', kind: 'countCurrency' },
  { key: 'subscriptionRenewals', group: 'Администраторы', label: 'Автопродления', kind: 'countCurrency' },
  { key: 'subscriptionUpgrades', group: 'Администраторы', label: 'Переходы на подписки', kind: 'countCurrency' },
  { key: 'subscriptionReturning', group: 'Администраторы', label: 'Возвраты в подписку', kind: 'countCurrency' },
  { key: 'productVisits', group: 'Продукт', label: 'Количество посещений', kind: 'count' }
]

const SHARED_DECISION_CELLS: Partial<Record<MetricKey, { decisionKey: MetricKey; rowSpan?: number; hidden?: boolean }>> = {
  marketing: { decisionKey: 'marketing', rowSpan: 2 },
  leads: { decisionKey: 'marketing', hidden: true },
  renewals: { decisionKey: 'renewals', rowSpan: 2 },
  upgrades: { decisionKey: 'renewals', hidden: true }
}

const SHARED_DECISION_MEMBER_KEYS: Partial<Record<MetricKey, MetricKey[]>> = {
  marketing: ['marketing', 'leads'],
  renewals: ['renewals', 'upgrades']
}

const WEEKLY_PAYMENT_COUNT_PLAN_FIELDS: (keyof WeeklyPlanVsFactRow)[] = [
  'weekly_new_sales_plan',
  'weekly_renewals_count_plan',
  'weekly_upgrades_count_plan',
  'weekly_returning_clients_count_plan',
  'weekly_new_subscriptions_count_plan',
  'weekly_subscription_renewals_count_plan',
  'weekly_subscription_upgrades_count_plan',
  'weekly_subscription_returning_clients_count_plan'
]

const WEEKLY_PAYMENT_COUNT_FACT_FIELDS: (keyof WeeklyPlanVsFactRow)[] = [
  'weekly_new_sales_fact',
  'weekly_renewals_count_fact',
  'weekly_upgrades_count_fact',
  'weekly_returning_clients_count_fact',
  'weekly_new_subscriptions_count_fact',
  'weekly_subscription_renewals_count_fact',
  'weekly_subscription_upgrades_count_fact',
  'weekly_subscription_returning_clients_count_fact'
]

const WEEKLY_NEW_PURCHASE_COUNT_PLAN_FIELDS: (keyof WeeklyPlanVsFactRow)[] = [
  'weekly_new_sales_plan',
  'weekly_new_subscriptions_count_plan'
]

const WEEKLY_NEW_PURCHASE_COUNT_FACT_FIELDS: (keyof WeeklyPlanVsFactRow)[] = [
  'weekly_new_sales_fact',
  'weekly_new_subscriptions_count_fact'
]

const DAILY_NEW_PURCHASE_COUNT_FIELDS: (keyof DailyTotals)[] = [
  'new_sales_count',
  'new_subscriptions_count'
]

const DAILY_PAYMENT_COUNT_FIELDS: (keyof DailyTotals)[] = [
  'new_sales_count',
  'renewals_count',
  'upgrades_count',
  'returning_clients_count',
  'new_subscriptions_count',
  'subscription_renewals_count',
  'subscription_upgrades_count',
  'subscription_returning_clients_count'
]

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

function toDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(date: string, days: number): string {
  const result = parseLocalDate(date)
  result.setDate(result.getDate() + days)
  return toDateKey(result)
}

function formatDateLabel(date: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(parseLocalDate(date))
}

function formatWeekday(date: string): string {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(parseLocalDate(date))
}

function rounded(value: number | undefined | null): number {
  return Math.round(value || 0)
}

function safeRate(numerator: number, denominator: number): number | null {
  if (!denominator) return null
  return (numerator / denominator) * 100
}

function sumOptionalFields<T extends WeeklyPlanVsFactRow | DailyTotals>(source: T | undefined, fields: (keyof T)[]): number | null {
  if (!source) return null
  let hasAnyValue = false
  let sum = 0
  for (const field of fields) {
    const value = source[field]
    if (typeof value === 'number') {
      hasAnyValue = true
      sum += value
    }
  }
  return hasAnyValue ? sum : null
}

function sumDailyFields(dates: string[], dailyByDate: Map<string, DailyTotals>, fields: (keyof DailyTotals)[]): number | null {
  let hasAnyValue = false
  let sum = 0
  for (const date of dates) {
    const day = dailyByDate.get(date)
    if (!day) continue
    for (const field of fields) {
      const value = day[field]
      if (typeof value === 'number') {
        hasAnyValue = true
        sum += value
      }
    }
  }
  return hasAnyValue ? sum : null
}

function ratioPercent(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null
  return (numerator / denominator) * 100
}

function divideMetric(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null
  return numerator / denominator
}

function getDailyMetricValue(
  day: DailyTotals | undefined,
  key: MetricKey,
  ratingDailyRows: RatingDailyRow[]
): MetricValue {
  if (key === 'productVisits') {
    return { count: ratingDailyRows.reduce((sum, row) => sum + (row.admissions_count || 0), 0) }
  }
  if (!day) return {}

  if (key === 'marketing') return { amount: day.marketing_expenses_amount || 0 }
  if (key === 'leads') return { count: day.leads_count || 0 }
  if (key === 'answerRate') return { value: safeRate(day.answered_calls_count || 0, day.calls_count || 0) }
  if (key === 'appointments') return { count: day.appointments_count || 0 }
  if (key === 'newVisitors') return { count: day.new_visitors_count || 0 }
  if (key === 'newSales') return { count: day.new_sales_count || 0, amount: day.new_sales_amount || 0 }
  if (key === 'renewals') return { count: day.renewals_count || 0, amount: day.renewals_amount || 0 }
  if (key === 'upgrades') return { count: day.upgrades_count || 0, amount: day.upgrades_amount || 0 }
  if (key === 'returning') return { count: day.returning_clients_count || 0, amount: day.returning_clients_amount || 0 }
  if (key === 'newSubscriptions') return { count: day.new_subscriptions_count || 0, amount: day.new_subscriptions_amount || 0 }
  if (key === 'subscriptionRenewals') return { count: day.subscription_renewals_count || 0, amount: day.subscription_renewals_amount || 0 }
  if (key === 'subscriptionUpgrades') return { count: day.subscription_upgrades_count || 0, amount: day.subscription_upgrades_amount || 0 }
  if (key === 'subscriptionReturning') return { count: day.subscription_returning_clients_count || 0, amount: day.subscription_returning_clients_amount || 0 }
  return {}
}

function getWeeklyPlanValue(week: WeeklyPlanVsFactRow | undefined, key: MetricKey): MetricValue {
  if (!week) return {}

  if (key === 'marketing') return { amount: week.weekly_marketing_budget_plan ?? undefined }
  if (key === 'leads') return { count: week.weekly_leads_plan ?? undefined }
  if (key === 'answerRate') return {}
  if (key === 'appointments') return { count: week.weekly_appointments_plan ?? undefined }
  if (key === 'newVisitors') return { count: week.weekly_new_visitors_plan ?? undefined }
  if (key === 'newSales') return { count: week.weekly_new_sales_plan ?? undefined, amount: week.weekly_new_sales_amount_plan ?? undefined }
  if (key === 'renewals') return { count: week.weekly_renewals_count_plan ?? undefined, amount: week.weekly_renewals_amount_plan ?? undefined }
  if (key === 'upgrades') return { count: week.weekly_upgrades_count_plan ?? undefined, amount: week.weekly_upgrades_amount_plan ?? undefined }
  if (key === 'returning') return { count: week.weekly_returning_clients_count_plan ?? undefined, amount: week.weekly_returning_clients_amount_plan ?? undefined }
  if (key === 'newSubscriptions') return { count: week.weekly_new_subscriptions_count_plan ?? undefined, amount: week.weekly_new_subscriptions_amount_plan ?? undefined }
  if (key === 'subscriptionRenewals') return { count: week.weekly_subscription_renewals_count_plan ?? undefined, amount: week.weekly_subscription_renewals_amount_plan ?? undefined }
  if (key === 'subscriptionUpgrades') return { count: week.weekly_subscription_upgrades_count_plan ?? undefined, amount: week.weekly_subscription_upgrades_amount_plan ?? undefined }
  if (key === 'subscriptionReturning') return { count: week.weekly_subscription_returning_clients_count_plan ?? undefined, amount: week.weekly_subscription_returning_clients_amount_plan ?? undefined }
  return {}
}

function sumMetricValues(values: MetricValue[], kind: MetricKind, dates: string[], dailyByDate: Map<string, DailyTotals>): MetricValue {
  if (kind === 'percent') {
    let calls = 0
    let answered = 0
    for (const date of dates) {
      const day = dailyByDate.get(date)
      calls += day?.calls_count || 0
      answered += day?.answered_calls_count || 0
    }
    return { value: safeRate(answered, calls) }
  }

  return values.reduce<MetricValue>((acc, value) => ({
    count: (acc.count || 0) + (value.count || 0),
    amount: (acc.amount || 0) + (value.amount || 0)
  }), {})
}

function hasValue(value: MetricValue, kind: MetricKind): boolean {
  if (kind === 'percent') return value.value !== null && value.value !== undefined
  if (kind === 'count') return value.count !== null && value.count !== undefined
  if (kind === 'currency') return value.amount !== null && value.amount !== undefined
  return (value.count !== null && value.count !== undefined) || (value.amount !== null && value.amount !== undefined)
}

function getComparableValue(value: MetricValue, kind: MetricKind): number | null {
  if (kind === 'percent') return value.value ?? null
  if (kind === 'count') return value.count ?? null
  if (kind === 'currency') return value.amount ?? null
  return value.amount ?? value.count ?? null
}

function formatMetricValue(value: MetricValue, kind: MetricKind): string {
  if (kind === 'percent') {
    if (value.value === null || value.value === undefined) return 'нет'
    return `${value.value.toFixed(1)}%`
  }
  if (kind === 'count') return formatNumber(rounded(value.count))
  if (kind === 'currency') return formatNumber(rounded(value.amount))
  return `${formatNumber(rounded(value.count))} / ${formatNumber(rounded(value.amount))}`
}

function getWeeklyFactOverride(
  key: MetricKey,
  ratingWeeklyRows: RatingWeeklyRow[]
): MetricValue | null {
  if (key === 'productVisits') return { count: ratingWeeklyRows.reduce((sum, row) => sum + (row.admissions_count || 0), 0) }
  return null
}

function formatKpiValue(value: number | null, kind: KpiKind, currencySymbol: string): string {
  if (value === null || Number.isNaN(value)) return 'нет'
  if (kind === 'percent') return `${value.toFixed(1)}%`
  if (kind === 'currency') return `${formatNumber(Math.round(value))} ${currencySymbol}`
  return formatNumber(Math.round(value))
}

function formatKpiDelta(delta: number | null, kind: KpiKind, currencySymbol: string): string {
  if (delta === null || Number.isNaN(delta)) return 'нет'
  const sign = delta > 0 ? '+' : ''
  if (kind === 'percent') return `${sign}${delta.toFixed(1)} п.п.`
  if (kind === 'currency') return `${sign}${formatNumber(Math.round(delta))} ${currencySymbol}`
  return `${sign}${formatNumber(Math.round(delta))}`
}

function formatKpiPercentDeviation(plan: number | null, fact: number | null): string {
  if (plan === null || fact === null || plan === 0) return 'нет'
  const deviation = ((fact - plan) / plan) * 100
  const sign = deviation > 0 ? '+' : ''
  return `${sign}${deviation.toFixed(1)}%`
}

function resizeTextarea(element: HTMLTextAreaElement | null) {
  if (!element) return
  element.style.height = '0px'
  element.style.height = `${element.scrollHeight}px`
}

function DecisionTextarea({ value, onChange, className = '', ...props }: DecisionTextareaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    resizeTextarea(ref.current)
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value)
    resizeTextarea(event.target)
  }

  return (
    <textarea
      {...props}
      ref={ref}
      data-decision-textarea="true"
      value={value}
      rows={1}
      onChange={handleChange}
      className={`block w-full min-h-[28px] resize-none overflow-hidden rounded-none bg-transparent px-2 py-1.5 text-xs text-foreground ${className}`}
    />
  )
}

function getDecisionPanelClass(tone: DecisionTone): string {
  if (tone === 'limiter') {
    return 'border-amber-200/70 bg-amber-50/75 text-amber-950 shadow-[0_1px_3px_rgba(146,64,14,0.08)] dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-100 dark:shadow-none'
  }
  return 'border-emerald-200/70 bg-emerald-50/75 text-emerald-950 shadow-[0_1px_3px_rgba(6,95,70,0.08)] dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-100 dark:shadow-none'
}

function getDecisionIconClass(tone: DecisionTone): string {
  return tone === 'limiter' ? 'text-amber-500/70 dark:text-amber-300/70' : 'text-emerald-500/70 dark:text-emerald-300/70'
}

/**
 * Технический отказ RPC переводится в причину на языке пользователя: человеку
 * нужно понять, что делать, а не увидеть текст исключения Postgres.
 */
function describeDecisionSaveError(message: string): string {
  if (/Only studio/i.test(message)) return 'Заполнять ограничители и решения могут владелец студии и управляющая. Изменения не сохранены.'
  if (/No access to studio/i.test(message)) return 'Нет доступа к этой студии — изменения не сохранены.'
  if (/Not authenticated/i.test(message)) return 'Сессия истекла. Войдите заново и повторите — изменения не сохранены.'
  if (/Failed to fetch|NetworkError|network/i.test(message)) return 'Нет связи с сервером — изменения не сохранены. Проверьте интернет и повторите.'
  return `Не удалось сохранить: ${message}`
}

function describeDecisionLoadError(message: string): string {
  if (/No access to studio/i.test(message)) return 'нет доступа к этой студии'
  if (/Not authenticated/i.test(message)) return 'сессия истекла, войдите заново'
  if (/Failed to fetch|NetworkError|network/i.test(message)) return 'нет связи с сервером'
  return message
}

function DecisionStatusIcon({ state, fallbackClass }: { state: DecisionSaveState; fallbackClass: string }) {
  if (state === 'saving') {
    return <Loader2 className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Сохраняем" />
  }
  if (state === 'saved') {
    return <Check className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" aria-label="Сохранено" />
  }
  if (state === 'error') {
    return <AlertCircle className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 text-destructive" aria-label="Не сохранено" />
  }
  return <Pencil className={`pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 ${fallbackClass}`} aria-hidden="true" />
}

function DecisionPanel({
  tone,
  value,
  placeholder,
  canEdit,
  saveState,
  onChange,
  onBlur
}: {
  tone: DecisionTone
  value: string
  placeholder: string
  canEdit: boolean
  saveState?: DecisionSaveState
  onChange: (value: string) => void
  onBlur: () => void
}) {
  const panelClass = getDecisionPanelClass(tone)
  const iconClass = getDecisionIconClass(tone)
  // Несохранённое обязано отличаться от сохранённого: до правки 08.09.2026 отказ
  // сервера не был виден нигде и текст выглядел принятым (см. CLAUDE.md).
  const errorClass = saveState === 'error' ? ' ring-1 ring-destructive border-destructive' : ''

  return (
    <div className={`relative min-h-[42px] rounded-md border px-2 py-1.5 ${panelClass}${errorClass}`}>
      {canEdit ? (
        <DecisionTextarea
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          className="pr-6 text-inherit placeholder:text-current/55 dark:placeholder:text-current/65"
          aria-label={placeholder}
        />
      ) : (
        <div className={`min-h-[28px] whitespace-pre-wrap pr-6 text-xs ${value ? 'text-inherit' : 'text-current/55 dark:text-current/65'}`}>
          {value || placeholder}
        </div>
      )}
      <DecisionStatusIcon state={canEdit ? saveState : undefined} fallbackClass={iconClass} />
    </div>
  )
}

function formatDelta(delta: number, kind: MetricKind): string {
  const sign = delta > 0 ? '+' : ''
  if (kind === 'percent') return `${sign}${delta.toFixed(1)} п.п.`
  return `${sign}${formatNumber(Math.round(delta))}`
}

function getDeltaTextClass(delta: number): string {
  if (delta > 0) return 'text-success'
  if (delta < 0) return 'text-error'
  return 'text-muted-foreground'
}

function getStatusInfo(percentDeviation: number | null) {
  if (percentDeviation === null) return { label: 'Нет данных', className: 'bg-muted-foreground/35' }
  if (percentDeviation >= -5) return { label: 'В пределах нормы', className: 'bg-success' }
  if (percentDeviation >= -15) return { label: 'Требует внимания', className: 'bg-warning' }
  return { label: 'Критично', className: 'bg-error' }
}

function formatMissingPlansMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) return `Не найден план студии №${studioNumbers[0]}`
  return `Не найдены планы студий №${studioNumbers.join(', ')}`
}

function formatZeroRevenueMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) return `Итоговая выручка студии №${studioNumbers[0]} равна 0`
  return `Итоговая выручка студий №${studioNumbers.join(', ')} равна 0`
}

function formatHideDeltaMessage(studioCount: number): string {
  if (studioCount <= 1) return 'Проверьте корректность плана по студии: итоговая выручка равна нулю.'
  return 'Проверьте корректность планов по студиям: итоговая выручка равна нулю.'
}

function formatZeroPlanRevenueMessage(studioNumbers: string[]): string {
  if (studioNumbers.length === 1) {
    return `Проверьте корректность плана по студии №${studioNumbers[0]}: итоговая выручка в плане равна нулю.`
  }
  return `Проверьте корректность планов по студиям №${studioNumbers.join(', ')}: итоговая выручка в плане равна нулю.`
}

function buildFallbackWeeks(dailyData: DailyTotals[]): WeekOption[] {
  if (dailyData.length === 0) return []
  const sortedDates = [...new Set(dailyData.map((day) => day.metric_date))].sort()
  const start = sortedDates[0]
  const end = sortedDates[sortedDates.length - 1]
  const weeks: WeekOption[] = []
  let weekStart = start
  let weekNumber = 1

  while (weekStart <= end) {
    const rawEnd = addDays(weekStart, 6)
    const weekEnd = rawEnd > end ? end : rawEnd
    const rangeDays = Math.floor((parseLocalDate(weekEnd).getTime() - parseLocalDate(weekStart).getTime()) / 86400000) + 1
    weeks.push({ week_number_in_period: weekNumber, week_start: weekStart, week_end: weekEnd, week_len_days: rangeDays })
    weekStart = addDays(weekStart, 7)
    weekNumber += 1
  }

  return weeks
}

function pickDefaultWeek(weeks: WeekOption[]): number | null {
  if (weeks.length === 0) return null
  const today = getLocalIsoDate()
  const currentWeek = weeks.find((week) => today >= week.week_start && today <= week.week_end)
  return currentWeek?.week_number_in_period || weeks[weeks.length - 1].week_number_in_period
}

export function WeeklyDepartmentMetricsTable({
  dailyData,
  planData,
  weeklyPlans,
  studioId = null,
  studioIds,
  canEditDecisions = false,
  canViewDecisions = canEditDecisions,
  missingPlanStudios = [],
  zeroRevenueStudios = [],
  zeroPlanRevenueStudios = [],
  hideDelta = false,
  hideDeltaStudioCount = 1,
  currency = 'RUB',
  selectedRangeMode = 'period',
  selectedRangeWeekNumber = null,
  onSelectedWeekNumberChange
}: WeeklyDepartmentMetricsTableProps) {
  const queryClient = useQueryClient()
  const currencySymbol = getCurrencySymbol(currency)
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<string, DecisionDraft>>({})
  const [saveStates, setSaveStates] = useState<Record<string, DecisionSaveState>>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const metricStudioIds = useMemo(
    () => (studioIds && studioIds.length > 0 ? studioIds : studioId ? [studioId] : []),
    [studioIds, studioId]
  )
  const metricStudioIdSet = useMemo(() => new Set(metricStudioIds), [metricStudioIds])

  const sortedDailyData = useMemo(
    () => [...dailyData].sort((a, b) => a.metric_date.localeCompare(b.metric_date)),
    [dailyData]
  )

  const dailyByDate = useMemo(() => {
    const result = new Map<string, DailyTotals>()
    for (const day of sortedDailyData) result.set(day.metric_date, day)
    return result
  }, [sortedDailyData])

  const weekOptions = useMemo<WeekOption[]>(() => {
    if (weeklyPlans.length > 0) {
      return [...weeklyPlans]
        .sort((a, b) => a.week_number_in_period - b.week_number_in_period)
        .map((week) => ({
          week_number_in_period: week.week_number_in_period,
          week_start: week.week_start,
          week_end: week.week_end,
          week_len_days: week.week_len_days || 7,
          source: week
        }))
    }
    return buildFallbackWeeks(sortedDailyData)
  }, [weeklyPlans, sortedDailyData])

  const syncedWeekNumber = selectedRangeMode === 'week' ? selectedRangeWeekNumber : null

  useEffect(() => {
    const desiredWeekNumber = syncedWeekNumber || pickDefaultWeek(weekOptions)
    const validDesired = desiredWeekNumber !== null && weekOptions.some((week) => week.week_number_in_period === desiredWeekNumber)
    const nextWeekNumber = validDesired ? desiredWeekNumber : pickDefaultWeek(weekOptions)
    if (selectedWeekNumber !== nextWeekNumber) setSelectedWeekNumber(nextWeekNumber)
  }, [selectedWeekNumber, syncedWeekNumber, selectedRangeMode, weekOptions])

  const selectedWeek = useMemo(
    () => weekOptions.find((week) => week.week_number_in_period === selectedWeekNumber) || weekOptions[0] || null,
    [selectedWeekNumber, weekOptions]
  )

  const weekDates = useMemo(() => {
    if (!selectedWeek) return []
    const dates: string[] = []
    const cursor = parseLocalDate(selectedWeek.week_start)
    const end = parseLocalDate(selectedWeek.week_end)
    while (cursor <= end) {
      dates.push(toDateKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    return dates
  }, [selectedWeek])

  const decisionPeriodId = selectedWeek?.source?.period_id || planData?.period_id || null
  const selectedWeekId = selectedWeek?.source?.week_id || null
  const decisionQueryKey = ['studio-weekly-metric-decisions', studioId, decisionPeriodId, selectedWeekNumber]
  const decisionsQuery = useQuery<DecisionRow[], Error>({
    queryKey: decisionQueryKey,
    queryFn: async () => {
      if (!canViewDecisions || !studioId || !decisionPeriodId || !selectedWeekNumber) return []
      const { data, error } = await supabase.rpc('get_studio_weekly_metric_decisions' as any, {
        p_studio_internal_id: studioId,
        p_period_id: decisionPeriodId,
        p_week_number_in_period: selectedWeekNumber
      })
      if (error) throw new Error(error.message)
      return (data || []) as DecisionRow[]
    },
    enabled: Boolean(canViewDecisions && studioId && decisionPeriodId && selectedWeekNumber)
  })

  const ratingDailyQuery = useQuery<RatingDailyRow[], Error>({
    queryKey: ['published-rating-daily-rows', metricStudioIds, selectedWeek?.week_start, selectedWeek?.week_end],
    queryFn: async () => {
      if (metricStudioIds.length === 0 || !selectedWeek) return []
      const { data, error } = await supabase.rpc('get_published_rating_daily_rows' as any, {
        p_start_date: selectedWeek.week_start,
        p_end_date: selectedWeek.week_end
      })
      if (error) throw new Error(error.message)
      return ((data || []) as RatingDailyRow[]).filter((row) => metricStudioIdSet.has(row.studio_internal_id))
    },
    enabled: Boolean(metricStudioIds.length > 0 && selectedWeek)
  })

  const ratingWeeklyQuery = useQuery<RatingWeeklyRow[], Error>({
    queryKey: ['published-rating-weekly-row', metricStudioIds, selectedWeekId],
    queryFn: async () => {
      if (metricStudioIds.length === 0 || !selectedWeekId) return []
      const { data, error } = await supabase.rpc('get_published_rating_rows' as any, {
        p_period_id: null,
        p_week_id: selectedWeekId
      })
      if (error) throw new Error(error.message)
      return ((data || []) as RatingWeeklyRow[]).filter((row) => metricStudioIdSet.has(row.studio_internal_id))
    },
    enabled: Boolean(metricStudioIds.length > 0 && selectedWeekId)
  })

  const savedDecisions = useMemo(() => {
    const next: Record<string, DecisionDraft> = {}
    for (const decision of decisionsQuery.data || []) {
      next[decision.metric_key] = { limiter: decision.limiter || '', decision: decision.decision || '' }
    }
    for (const [decisionKey, memberKeys] of Object.entries(SHARED_DECISION_MEMBER_KEYS)) {
      const firstFilledDraft = memberKeys?.map((key) => next[key]).find((draft) => draft && (draft.limiter || draft.decision))
      if (firstFilledDraft) next[decisionKey] = firstFilledDraft
    }
    return next
  }, [decisionsQuery.data])

  useEffect(() => {
    setDrafts(savedDecisions)
  }, [savedDecisions])

  // Смена студии/недели начинает новый лист: старые отметки «сохранено» и текст
  // ошибки к нему не относятся.
  useEffect(() => {
    setSaveStates({})
    setSaveError(null)
  }, [studioId, decisionPeriodId, selectedWeekNumber])

  const saveDecisionMutation = useMutation({
    mutationFn: async (payload: { metricKey: string; limiter: string; decision: string }) => {
      // Молча не выходим: до правки 08.09.2026 недостающий период и отказ сервера
      // одинаково выглядели как «сохранено», и работа терялась при смене студии.
      if (!canEditDecisions) throw new Error('Недостаточно прав для изменения ограничителей и решений')
      if (!studioId) throw new Error('Студия не выбрана')
      if (!decisionPeriodId || !selectedWeekNumber) throw new Error('Не определён период недели — обновите страницу')
      const memberKeys = SHARED_DECISION_MEMBER_KEYS[payload.metricKey as MetricKey] || [payload.metricKey]
      await Promise.all(memberKeys.map(async (metricKey) => {
        const { error } = await supabase.rpc('set_studio_weekly_metric_decision' as any, {
          p_studio_internal_id: studioId,
          p_period_id: decisionPeriodId,
          p_week_number_in_period: selectedWeekNumber,
          p_metric_key: metricKey,
          p_limiter: payload.limiter,
          p_decision: payload.decision
        })
        if (error) throw new Error(error.message)
      }))
    },
    onMutate: (payload) => {
      setSaveStates((prev) => ({ ...prev, [payload.metricKey]: 'saving' }))
      setSaveError(null)
    },
    onSuccess: (_result, payload) => {
      setSaveStates((prev) => ({ ...prev, [payload.metricKey]: 'saved' }))
      queryClient.invalidateQueries({ queryKey: decisionQueryKey })
    },
    onError: (error: Error, payload) => {
      setSaveStates((prev) => ({ ...prev, [payload.metricKey]: 'error' }))
      setSaveError(describeDecisionSaveError(error.message))
    }
  })

  // Отказ чтения тоже был невидим: пустые поля читались как «записей нет».
  const decisionLoadError = decisionsQuery.error ? describeDecisionLoadError(decisionsQuery.error.message) : null

  const submitDecision = (metricKey: string) => {
    const draft = drafts[metricKey] || { limiter: '', decision: '' }
    const saved = savedDecisions[metricKey] || { limiter: '', decision: '' }
    // Уход из поля без правки не должен слать запрос — иначе экран сыплет
    // ошибками там, где человек просто прошёл по ячейкам взглядом.
    if (draft.limiter === saved.limiter && draft.decision === saved.decision) return
    saveDecisionMutation.mutate({ metricKey, limiter: draft.limiter, decision: draft.decision })
  }

  if (!selectedWeek || weekDates.length === 0) return null

  const ratingDailyByDate = new Map<string, RatingDailyRow[]>()
  for (const row of ratingDailyQuery.data || []) {
    const dateKey = String(row.metric_date)
    const rows = ratingDailyByDate.get(dateKey) || []
    rows.push(row)
    ratingDailyByDate.set(dateKey, rows)
  }

  const activeRows = METRICS
    .map((metric) => {
      const dailyValues = weekDates.map((date) => (
        getDailyMetricValue(dailyByDate.get(date), metric.key, ratingDailyByDate.get(date) || [])
      ))
      const fact = getWeeklyFactOverride(metric.key, ratingWeeklyQuery.data || [])
        || sumMetricValues(dailyValues, metric.kind, weekDates, dailyByDate)
      const plan = getWeeklyPlanValue(selectedWeek.source, metric.key)
      const hasPlan = hasValue(plan, metric.kind)
      const factBase = getComparableValue(fact, metric.kind)
      const planBase = getComparableValue(plan, metric.kind)
      const delta = factBase !== null && planBase !== null ? factBase - planBase : null
      const percentDeviation = delta !== null && planBase ? (delta / planBase) * 100 : null
      return { metric, dailyValues, fact, plan, hasPlan, delta, percentDeviation }
    })

  if (activeRows.length === 0) return null

  const totalColumnCount = weekDates.length + 6 + (canViewDecisions ? 2 : 0)

  const weeklyRevenuePlan = selectedWeek.source?.weekly_gross_revenue_plan ?? null
  const weeklyRevenueFact = selectedWeek.source?.weekly_gross_revenue_fact ?? sumDailyFields(weekDates, dailyByDate, ['gross_revenue'])
  const weeklyPaymentCountPlan = sumOptionalFields(selectedWeek.source, WEEKLY_PAYMENT_COUNT_PLAN_FIELDS)
  const weeklyPaymentCountFact = sumOptionalFields(selectedWeek.source, WEEKLY_PAYMENT_COUNT_FACT_FIELDS)
    ?? sumDailyFields(weekDates, dailyByDate, DAILY_PAYMENT_COUNT_FIELDS)
  // Та же база «покупки», что у сквозной конверсии заявка → покупка выше:
  // только новые абонементы и первые подписки, без продлений и апгрейдов.
  const weeklyNewPurchaseCountPlan = sumOptionalFields(selectedWeek.source, WEEKLY_NEW_PURCHASE_COUNT_PLAN_FIELDS)
  const weeklyNewPurchaseCountFact = sumOptionalFields(selectedWeek.source, WEEKLY_NEW_PURCHASE_COUNT_FACT_FIELDS)
    ?? sumDailyFields(weekDates, dailyByDate, DAILY_NEW_PURCHASE_COUNT_FIELDS)
  const weeklyLeadsPlan = selectedWeek.source?.weekly_leads_plan ?? null
  const weeklyLeadsFact = selectedWeek.source?.weekly_leads_fact ?? sumDailyFields(weekDates, dailyByDate, ['leads_count'])
  const weeklyMarketingPlan = selectedWeek.source?.weekly_marketing_budget_plan ?? null
  const weeklyMarketingFact = selectedWeek.source?.weekly_marketing_expenses_amount_fact ?? sumDailyFields(weekDates, dailyByDate, ['marketing_expenses_amount'])
  const kpiCards: KpiCard[] = [
    { key: 'revenue', label: 'Выручка', kind: 'currency', plan: weeklyRevenuePlan, fact: weeklyRevenueFact },
    { key: 'paymentCount', label: 'Количество оплат (новые + постоянные)', kind: 'count', plan: weeklyPaymentCountPlan, fact: weeklyPaymentCountFact },
    {
      key: 'avgCheck',
      label: 'Средний чек всех оплат',
      kind: 'currency',
      plan: divideMetric(weeklyRevenuePlan, weeklyPaymentCountPlan),
      fact: divideMetric(weeklyRevenueFact, weeklyPaymentCountFact)
    },
    {
      key: 'leadToPurchase',
      label: '% лид-покупка по новым',
      kind: 'percent',
      plan: ratioPercent(weeklyNewPurchaseCountPlan, weeklyLeadsPlan),
      fact: ratioPercent(weeklyNewPurchaseCountFact, weeklyLeadsFact)
    },
    {
      key: 'cac',
      label: 'CAC',
      kind: 'currency',
      plan: divideMetric(weeklyMarketingPlan, weeklyNewPurchaseCountPlan),
      fact: divideMetric(weeklyMarketingFact, weeklyNewPurchaseCountFact)
    }
  ]

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2 mb-3">
        {kpiCards.map((card) => {
          const delta = card.plan !== null && card.fact !== null ? card.fact - card.plan : null
          const deltaClass = delta !== null ? getDeltaTextClass(delta) : 'text-muted-foreground'

          return (
            <div key={card.key} className="rounded-md border border-border bg-card px-3 py-2.5">
              <div className="text-xs font-medium text-foreground/85 truncate">{card.label}</div>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase text-muted-foreground/75">План</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground truncate">{formatKpiValue(card.plan, card.kind, currencySymbol)}</div>
                  <div className={`mt-0.5 text-[11px] font-medium truncate ${deltaClass}`}>
                    Откл. {hideDelta ? 'нет' : formatKpiDelta(delta, card.kind, currencySymbol)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase text-muted-foreground/75">Факт</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground truncate">{formatKpiValue(card.fact, card.kind, currencySymbol)}</div>
                  <div className={`mt-0.5 text-[11px] font-medium truncate ${deltaClass}`}>
                    Откл. {hideDelta ? 'нет' : formatKpiPercentDeviation(card.plan, card.fact)}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <Card className="overflow-hidden isolate">
      <div className="px-4 pt-4 pb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          {missingPlanStudios.length > 0 && <p className="text-xs text-warning font-semibold mb-2">{formatMissingPlansMessage(missingPlanStudios)}</p>}
          {zeroRevenueStudios.length > 0 && <p className="text-xs text-warning font-semibold mb-2">{formatZeroRevenueMessage(zeroRevenueStudios)}</p>}
          {zeroPlanRevenueStudios.length > 0 && <p className="text-xs text-warning font-semibold mb-2">{formatZeroPlanRevenueMessage(zeroPlanRevenueStudios)}</p>}
          {hideDelta && zeroPlanRevenueStudios.length === 0 && <p className="text-xs text-warning font-semibold mb-2">{formatHideDeltaMessage(hideDeltaStudioCount)}</p>}
          <h3 className="text-sm font-medium text-foreground/90">Данные по дням и отделам</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground/85 mt-1">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-success" /> в пределах нормы</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-warning" /> требует внимания</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-error" /> критично</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-muted-foreground/35" /> нет данных</span>
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Неделя
          <select
            value={selectedWeek.week_number_in_period}
            onChange={(event) => {
              const nextWeekNumber = Number(event.target.value)
              setSelectedWeekNumber(nextWeekNumber)
              onSelectedWeekNumberChange?.(nextWeekNumber)
            }}
            className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground"
          >
            {weekOptions.map((week) => (
              <option key={week.week_number_in_period} value={week.week_number_in_period}>
                Неделя {week.week_number_in_period}: {formatDateLabel(week.week_start)} - {formatDateLabel(week.week_end)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {canViewDecisions && decisionLoadError && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Ограничители и решения не загрузились: {decisionLoadError}. Поля показаны пустыми — это не значит, что записей нет.</span>
        </div>
      )}
      {saveError && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{saveError}</span>
        </div>
      )}

      <div className="overflow-auto max-h-[calc(100vh-180px)]" style={{ maxHeight: 'calc(100dvh - 180px)' }}>
        <table className="w-full min-w-[1680px] border-collapse">
          <thead className="tg-table-header border-y border-border">
            <tr>
              <th className="text-left px-2.5 py-2 text-[11px] font-normal text-muted-foreground/80 sticky top-0 left-0 z-30 bg-muted border-r border-border min-w-[220px]">
                Отдел / Метрика
              </th>
              {weekDates.map((date) => (
                <th key={date} className="text-center px-2.5 py-2 text-[11px] font-normal text-muted-foreground/80 sticky top-0 z-20 bg-muted border-r border-border min-w-[112px]">
                  <div className="font-semibold text-foreground/80">{formatDateLabel(date)}</div>
                  <div className="mt-0.5">{formatWeekday(date)}</div>
                </th>
              ))}
              <th colSpan={4} className="text-center px-2.5 py-2 text-[11px] font-normal text-muted-foreground/80 sticky top-0 z-20 bg-muted border-r border-border">
                Итого за неделю
              </th>
              <th className="text-center px-2.5 py-2 text-[11px] font-normal text-muted-foreground/80 sticky top-0 z-20 bg-muted border-r border-border min-w-[78px]">
                Статус
              </th>
              {canViewDecisions && (
                <th colSpan={2} className="text-center px-2.5 py-2 text-[12px] font-semibold text-foreground/80 sticky top-0 z-20 bg-muted min-w-[430px]">
                  <span className="inline-flex items-center justify-center gap-1.5">
                    Разбор отклонения
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />
                  </span>
                </th>
              )}
            </tr>
            <tr>
              <th className="sticky top-[41px] left-0 z-30 bg-muted border-r border-border p-0" />
              {weekDates.map((date) => <th key={date} className="sticky top-[41px] z-20 bg-muted border-r border-border p-0" />)}
              <th className="text-right px-2 py-1.5 text-[10px] font-normal text-muted-foreground/80 sticky top-[41px] z-20 bg-muted border-r border-border">Факт</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-normal text-muted-foreground/80 sticky top-[41px] z-20 bg-muted border-r border-border">План</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-normal text-muted-foreground/80 sticky top-[41px] z-20 bg-muted border-r border-border">Отклонение</th>
              <th className="text-right px-2 py-1.5 text-[10px] font-normal text-muted-foreground/80 sticky top-[41px] z-20 bg-muted border-r border-border">%</th>
              <th className="sticky top-[41px] z-20 bg-muted border-r border-border p-0" />
              {canViewDecisions && (
                <>
                  <th className="text-center px-2 py-1.5 text-[11px] font-semibold sticky top-[41px] z-20 bg-muted min-w-[210px]">
                    <span className="inline-flex items-center justify-center gap-1 text-amber-700 dark:text-amber-200">
                      Ограничитель
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  </th>
                  <th className="text-center px-2 py-1.5 text-[11px] font-semibold sticky top-[41px] z-20 bg-muted min-w-[220px]">
                    <span className="inline-flex items-center justify-center gap-1 text-emerald-700 dark:text-emerald-200">
                      Решение
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {activeRows.map((row, index) => {
              const previousGroup = activeRows[index - 1]?.metric.group
              const showGroup = previousGroup !== row.metric.group
              const sharedDecisionCell = SHARED_DECISION_CELLS[row.metric.key]
              const decisionKey = sharedDecisionCell?.decisionKey || row.metric.key
              const draft = drafts[decisionKey] || { limiter: '', decision: '' }
              const status = getStatusInfo(row.percentDeviation)

              return (
                <Fragment key={row.metric.key}>
                  {showGroup && (
                    <tr className="bg-muted/40 border-t border-border">
                      <td colSpan={totalColumnCount} className="px-2.5 py-1.5 text-xs font-semibold text-foreground sticky left-0 z-10 bg-muted/95">
                        {row.metric.group}
                      </td>
                    </tr>
                  )}
                  <tr className="last:border-b-0">
                    <td className="px-2.5 py-1.5 text-xs font-medium whitespace-nowrap sticky left-0 z-10 bg-card border-r border-b border-border">
                      {row.metric.label}
                      {row.metric.kind === 'currency' && <span className="ml-1 text-[11px] text-muted-foreground">({currencySymbol})</span>}
                      {row.metric.kind === 'count' && <span className="ml-1 text-[11px] text-muted-foreground">(шт)</span>}
                      {row.metric.kind === 'countCurrency' && <span className="ml-1 text-[11px] text-muted-foreground">(шт / {currencySymbol})</span>}
                    </td>
                    {row.dailyValues.map((value, dayIndex) => (
                      <td key={`${row.metric.key}-${weekDates[dayIndex]}`} className="px-2.5 py-1.5 text-xs text-right border-r border-b border-border">
                        {formatMetricValue(value, row.metric.kind)}
                      </td>
                    ))}
                    <td className="px-2.5 py-1.5 text-xs text-right font-semibold border-r border-b border-border">
                      {formatMetricValue(row.fact, row.metric.kind)}
                    </td>
                    <td className="px-2.5 py-1.5 text-xs text-right text-muted-foreground border-r border-b border-border">
                      {row.hasPlan ? formatMetricValue(row.plan, row.metric.kind) : 'нет'}
                    </td>
                    <td className={`px-2.5 py-1.5 text-xs text-right font-semibold border-r border-b border-border ${row.delta !== null ? getDeltaTextClass(row.delta) : 'text-muted-foreground'}`}>
                      {row.delta !== null && !hideDelta ? formatDelta(row.delta, row.metric.kind) : 'нет'}
                    </td>
                    <td className={`px-2.5 py-1.5 text-xs text-right font-semibold border-r border-b border-border ${row.delta !== null ? getDeltaTextClass(row.delta) : 'text-muted-foreground'}`}>
                      {row.percentDeviation !== null && !hideDelta ? `${row.percentDeviation > 0 ? '+' : ''}${row.percentDeviation.toFixed(1)}%` : 'нет'}
                    </td>
                    <td className="px-2.5 py-1.5 text-center border-r border-b border-border">
                      <span title={status.label} className={`inline-block w-2.5 h-2.5 rounded-full ${status.className}`} />
                    </td>
                    {canViewDecisions && !sharedDecisionCell?.hidden && (
                      <>
                        <td
                          rowSpan={sharedDecisionCell?.rowSpan}
                          className={`p-1.5 bg-muted/45 dark:bg-muted/20 ${sharedDecisionCell?.rowSpan ? 'align-middle' : 'align-top'}`}
                        >
                          <DecisionPanel
                            tone="limiter"
                            value={draft.limiter}
                            placeholder="Укажите ограничитель"
                            canEdit={canEditDecisions}
                            saveState={saveStates[decisionKey]}
                            onChange={(value) => {
                              setDrafts((prev) => ({ ...prev, [decisionKey]: { ...draft, limiter: value } }))
                              setSaveStates((prev) => ({ ...prev, [decisionKey]: undefined }))
                            }}
                            onBlur={() => submitDecision(decisionKey)}
                          />
                        </td>
                        <td
                          rowSpan={sharedDecisionCell?.rowSpan}
                          className={`p-1.5 bg-muted/45 dark:bg-muted/20 ${sharedDecisionCell?.rowSpan ? 'align-middle' : 'align-top'}`}
                        >
                          <DecisionPanel
                            tone="decision"
                            value={draft.decision}
                            placeholder="Укажите решение"
                            canEdit={canEditDecisions}
                            saveState={saveStates[decisionKey]}
                            onChange={(value) => {
                              setDrafts((prev) => ({ ...prev, [decisionKey]: { ...draft, decision: value } }))
                              setSaveStates((prev) => ({ ...prev, [decisionKey]: undefined }))
                            }}
                            onBlur={() => submitDecision(decisionKey)}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      </Card>
    </>
  )
}
